import React, { useEffect, useState, useCallback } from "react";
import { facilityAdmin } from "../../api";
import {
  PageHeader, Btn, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";
import BRAND from "../../brand";
import ZonesModernView from "./ZonesModernView";

export default function ZonesPage() {
  const [zones,    setZones]   = useState([]);
  const [stations, setStations]= useState([]);
  const [loading,  setLoading] = useState(true);
  const [expanded, setExpanded]= useState({});

  const [zoneModal,    setZoneModal]    = useState(null);
  const [stationModal, setStationModal] = useState(null);
  const [counterModal, setCounterModal] = useState(null);
  const [outcomeModal, setOutcomeModal] = useState(null);
  const [moveModal,    setMoveModal]    = useState(null);
  const [deleteConfirm,setDeleteConfirm]= useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [zr, sr] = await Promise.all([
        facilityAdmin.listZones(),
        facilityAdmin.listStations(),
      ]);
      const zoneList = zr.data?.results || zr.data;
      setZones(zoneList);
      setStations(sr.data?.results || sr.data);

      // FIX 3: expand ALL zones by default, not just the first one
      const allExpanded = {};
      zoneList.forEach(z => { allExpanded[z.id] = true; });
      setExpanded(allExpanded);
    } catch { toast.error("Failed to load zones."); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stationsForZone = (zoneId) =>
    stations.filter(s => s.zone === zoneId || s.zone_id === zoneId);

  const saveZone = async (form) => {
    setSaving(true);
    try {
      if (form.id) await facilityAdmin.updateZone(form.id, form);
      else         await facilityAdmin.createZone(form);
      toast.success(`Zone ${form.id ? "updated" : "created"}.`);
      setZoneModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.name?.[0] || "Failed to save zone.");
    } finally { setSaving(false); }
  };

  const saveStation = async (form) => {
    setSaving(true);
    try {
      if (form.id) await facilityAdmin.updateStation(form.id, form);
      else         await facilityAdmin.createStation(form);
      toast.success(`Station ${form.id ? "updated" : "created"}.`);
      setStationModal(null); load();
    } catch (e) {
      const msg = e.response?.data?.detail
        || e.response?.data?.non_field_errors?.[0]
        || "Failed to save station.";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const saveCounter = async (form) => {
    setSaving(true);
    try {
      if (form.id) await facilityAdmin.updateCounter(form.id, form);
      else         await facilityAdmin.createCounter(form);
      toast.success(`Counter ${form.id ? "updated" : "created"}.`);
      setCounterModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save counter.");
    } finally { setSaving(false); }
  };

  const handleMoveCounter = async (targetStationId) => {
    if (!moveModal) return;
    setSaving(true);
    try {
      await facilityAdmin.moveCounter(moveModal.id, targetStationId);
      toast.success(`${moveModal.name} moved.`);
      setMoveModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to move counter.");
    } finally { setSaving(false); }
  };

  const saveOutcome = async (form) => {
    setSaving(true);
    try {
      if (form.id) await facilityAdmin.updateOutcome(form.id, form);
      else         await facilityAdmin.createOutcome(form);
      toast.success(`Outcome ${form.id ? "updated" : "created"}.`);
      setOutcomeModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save outcome.");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    try {
      const { type, item } = deleteConfirm;
      if (type === "zone")    await facilityAdmin.deleteZone(item.id);
      if (type === "station") await facilityAdmin.deleteStation(item.id);
      if (type === "counter") await facilityAdmin.deleteCounter(item.id);
      if (type === "outcome") await facilityAdmin.deleteOutcome(item.id);
      toast.success("Deleted.");
      setDeleteConfirm(null); load();
    } catch { toast.error("Delete failed — item may have active tickets."); }
    finally  { setSaving(false); }
  };

  const modals = (
    <>
      <ZoneFormModal open={!!zoneModal} onClose={() => setZoneModal(null)} initial={zoneModal !== "create" ? zoneModal : null} onSave={saveZone} saving={saving} />
      <StationFormModal open={!!stationModal} onClose={() => setStationModal(null)} initial={stationModal?.id ? stationModal : null} zoneId={stationModal?.zoneId || stationModal?.zone} zones={zones} onSave={saveStation} saving={saving} />
      <CounterFormModal open={!!counterModal} onClose={() => setCounterModal(null)} initial={counterModal?.id ? counterModal : null} stationId={counterModal?.stationId || counterModal?.station} stationName={counterModal?.stationName} onSave={saveCounter} saving={saving} />
      <OutcomeFormModal open={!!outcomeModal} onClose={() => setOutcomeModal(null)} initial={outcomeModal?.id ? outcomeModal : null} stationId={outcomeModal?.stationId || outcomeModal?.station} stationName={outcomeModal?.stationName} allStations={outcomeModal?.allStations || stations} onSave={saveOutcome} saving={saving} />
      <MoveCounterModal open={!!moveModal} onClose={() => setMoveModal(null)} counter={moveModal} stations={stations} onMove={handleMoveCounter} saving={saving} />
      <ConfirmModal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} onConfirm={handleDelete} loading={saving} title={`Delete ${deleteConfirm?.type}?`} message={`"${deleteConfirm?.item?.name || deleteConfirm?.item?.label_en}" will be permanently deleted.`} confirmLabel="Delete" variant="danger" />
    </>
  );

  if (BRAND.dashboard === "modern") {
    return (
      <>
        <ZonesModernView
          loading={loading} zones={zones} stations={stations} expanded={expanded} setExpanded={setExpanded} load={load}
          setZoneModal={setZoneModal} setStationModal={setStationModal} setCounterModal={setCounterModal}
          setOutcomeModal={setOutcomeModal} setMoveModal={setMoveModal} setDeleteConfirm={setDeleteConfirm}
        />
        {modals}
      </>
    );
  }

  return (
    <div style={styles.root}>
      <PageHeader
        title="Zones & Stations"
        subtitle="Define your facility's zones, service stations, counters, and patient routing outcomes."
        action={<Btn onClick={() => setZoneModal("create")}>+ New Zone</Btn>}
      />

      {loading ? <LoadingSpinner /> : zones.length === 0 ? (
        <EmptyState
          icon="🗂️" title="No zones yet"
          subtitle="Create a zone (e.g. OPD Block) then add stations inside it."
          action={<Btn onClick={() => setZoneModal("create")}>+ Create First Zone</Btn>}
        />
      ) : zones.map(zone => (
        <div key={zone.id} style={styles.zoneCard}>
          <div style={styles.zoneHeader}>
            <button style={styles.expandBtn}
              onClick={() => setExpanded(e => ({ ...e, [zone.id]: !e[zone.id] }))}>
              <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                {expanded[zone.id] ? "▾" : "▸"}
              </span>
            </button>
            <div style={styles.zoneInfo}>
              <span style={styles.zoneName}>{zone.name}</span>
              {zone.description && <span style={styles.zoneDesc}>{zone.description}</span>}
              <span style={styles.zoneCount}>
                {stationsForZone(zone.id).length} station{stationsForZone(zone.id).length !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={styles.rowActions}>
              <Btn size="sm" variant="ghost" onClick={() => setStationModal({ zoneId: zone.id })}>+ Station</Btn>
              <Btn size="sm" variant="ghost" onClick={() => setZoneModal(zone)}>Edit</Btn>
              <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm({ type: "zone", item: zone })}>Delete</Btn>
            </div>
          </div>

          {expanded[zone.id] && (
            <div style={styles.stationList}>
              {stationsForZone(zone.id).length === 0 ? (
                <div style={styles.emptyInner}>No stations — click "+ Station" to add one.</div>
                 ) : stationsForZone(zone.id).map(st => (
                   <>
                     <div key={st.id} style={styles.stationRow}>
                  <div style={styles.stationLeft}>
                    <span style={styles.stationBadge}>{st.queue_prefix}</span>
                    <div>
                      <div style={styles.stationName}>
                        {st.name}
                        {st.is_entry_point && <span style={styles.entryChip}>Entry Point</span>}
                      </div>
                      <div style={styles.stationMeta}>
                        {st.counter_count ?? (st.counters?.length ?? 0)} counter{(st.counter_count ?? st.counters?.length ?? 0) !== 1 ? "s" : ""}
                        {" · "}
                        {st.outcomes?.length ?? 0} outcome{(st.outcomes?.length ?? 0) !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                  <div style={styles.rowActions}>
                    <Btn size="sm" variant="ghost" onClick={() => setCounterModal({ stationId: st.id, stationName: st.name })}>+ Counter</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setOutcomeModal({ stationId: st.id, stationName: st.name, allStations: stations })}>+ Outcome</Btn>
                    <Btn size="sm" variant="ghost" onClick={() => setStationModal({ ...st, zoneId: zone.id })}>Edit</Btn>
                    <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm({ type: "station", item: st })}>Delete</Btn>
                  </div>
                </div>

                {/* ── Counter rows — expandable ── */}
                {st.counters?.length > 0 && (
                  <div style={styles.counterList}>
                    {st.counters.map(counter => (
                      <div key={counter.id} style={styles.counterRow}>
                        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                          <span style={{ fontSize:"11px", fontWeight:"700", color:"var(--text-secondary)", fontFamily:"monospace", minWidth:"24px" }}>#{counter.number}</span>
                          <span style={{ fontSize:"13px", color:"var(--text-primary)", fontWeight:"500" }}>{counter.name}</span>
                          <span style={{ fontSize:"11px", padding:"2px 8px", borderRadius:"10px", fontWeight:"600",
                            background: counter.is_active !== false ? "rgba(34,197,94,0.1)" : "rgba(100,116,139,0.1)",
                            color:      counter.is_active !== false ? "#22C55E" : "var(--text-secondary)",
                            border:     `1px solid ${counter.is_active !== false ? "rgba(34,197,94,0.2)" : "rgba(100,116,139,0.2)"}`,
                          }}>
                            {counter.is_active !== false ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div style={{ display:"flex", gap:"8px" }}>
                          <Btn size="sm" variant="ghost" onClick={() => setCounterModal({ ...counter, stationId: st.id, stationName: st.name })}>Edit</Btn>
                          <Btn size="sm" variant="ghost" onClick={() => setMoveModal({ ...counter, currentStationId: st.id, currentStationName: st.name })}>Move</Btn>
                          <Btn size="sm" variant="ghost" onClick={async () => {
                            try {
                              await facilityAdmin.updateCounter(counter.id, { is_active: counter.is_active === false });
                              toast.success(`Counter ${counter.is_active === false ? "activated" : "deactivated"}.`);
                              load();
                            } catch { toast.error("Failed to update counter."); }
                          }}>
                            {counter.is_active !== false ? "Deactivate" : "Activate"}
                          </Btn>
                          <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm({ type:"counter", item: counter })}>Delete</Btn>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Outcome rows — expandable ── */}
                {st.outcomes?.length > 0 && (
                  <div style={styles.counterList}>
                    {st.outcomes.map(outcome => (
                      <div key={outcome.id} style={styles.counterRow}>
                        <div style={{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" }}>
                          <span style={{ fontSize:"13px", color:"var(--text-primary)", fontWeight:"500" }}>{outcome.label_en}</span>
                          {outcome.is_exit && (
                            <span style={{ fontSize:"10px", padding:"2px 7px", borderRadius:"5px", fontWeight:"700", background:"rgba(34,197,94,0.1)", color:"#22C55E", border:"1px solid rgba(34,197,94,0.2)" }}>EXIT</span>
                          )}
                          {outcome.is_emergency && (
                            <span style={{ fontSize:"10px", padding:"2px 7px", borderRadius:"5px", fontWeight:"700", background:"rgba(239,68,68,0.1)", color:"#EF4444", border:"1px solid rgba(239,68,68,0.2)" }}>EMERGENCY</span>
                          )}
                          {outcome.next_station_name && (
                            <span style={{ fontSize:"11px", color:"var(--text-muted)" }}>→ {outcome.next_station_name}</span>
                          )}
                        </div>
                        <div style={{ display:"flex", gap:"8px" }}>
                          <Btn size="sm" variant="ghost" onClick={() => setOutcomeModal({ ...outcome, stationId: st.id, stationName: st.name, allStations: stations })}>Edit</Btn>
                          <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm({ type:"outcome", item: outcome })}>Delete</Btn>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
               </>
              ))}
            </div>
          )}
        </div>
      ))}

      {modals}
    </div>
  );
}

function ZoneFormModal({ open, onClose, initial, onSave, saving }) {
  const [form, setForm] = useState({ name: "", description: "", direction_note_en: "", direction_note_tw: "" });
  useEffect(() => { if (open) setForm(initial ? { name: initial.name, description: initial.description || "", direction_note_en: initial.direction_note_en || "", direction_note_tw: initial.direction_note_tw || "" } : { name: "", description: "", direction_note_en: "", direction_note_tw: "" }); }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit Zone" : "New Zone"} width="460px">
      <div style={F.form}>
        <FormField label="Zone Name" required><input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. OPD Block, Ground Floor" /></FormField>
        <FormField label="Description"><input style={inputStyle} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Brief description" /></FormField>
        <FormField label="Walking Directions (English)" hint="Shown in SMS notifications"><input style={inputStyle} value={form.direction_note_en} onChange={e => set("direction_note_en", e.target.value)} placeholder="e.g. Walk straight, turn left at pharmacy" /></FormField>
        {!BRAND.englishOnly && <FormField label="Walking Directions (Twi)"><input style={inputStyle} value={form.direction_note_tw} onChange={e => set("direction_note_tw", e.target.value)} placeholder="Twi directions" /></FormField>}
        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onSave(initial ? { ...form, id: initial.id } : form)} disabled={saving || !form.name.trim()}>{saving ? "Saving…" : initial ? "Save Changes" : "Create Zone"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function StationFormModal({ open, onClose, initial, zoneId, zones, onSave, saving }) {
  const blank = { name: "", code: "", queue_prefix: "Q", is_entry_point: false, announcement_name_en: "", announcement_name_tw: "", sort_order: 0 };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) setForm(initial ? { ...initial } : { ...blank, zone: zoneId }); }, [open, initial, zoneId]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit Station" : "New Station"} width="500px">
      <div style={F.form}>
        <div style={F.row2}>
          <FormField label="Station Name" required><input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Customer Care" /></FormField>
          <FormField label="Code" required hint="Short code e.g. CC, OPD"><input style={inputStyle} value={form.code} onChange={e => set("code", e.target.value.toUpperCase())} placeholder="CC" /></FormField>
        </div>
        <div style={F.row2}>
          <FormField label="Queue Prefix" hint="Ticket prefix e.g. Q, N, C"><input style={inputStyle} value={form.queue_prefix} onChange={e => set("queue_prefix", e.target.value.toUpperCase())} placeholder="Q" maxLength={5} /></FormField>
          <FormField label="Zone" required>
            <select style={selectStyle} value={form.zone || zoneId} onChange={e => set("zone", e.target.value)}>
              {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </FormField>
        </div>
        <FormField label="Spoken Name (English)" hint="How announcements say this station"><input style={inputStyle} value={form.announcement_name_en} onChange={e => set("announcement_name_en", e.target.value)} placeholder="e.g. Customer Care Counter" /></FormField>
        {!BRAND.englishOnly && <FormField label="Spoken Name (Twi)"><input style={inputStyle} value={form.announcement_name_tw} onChange={e => set("announcement_name_tw", e.target.value)} placeholder="Twi spoken name" /></FormField>}
        <label style={F.checkbox}>
          <input type="checkbox" checked={form.is_entry_point} onChange={e => set("is_entry_point", e.target.checked)} />
          <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Entry point — patients routed here directly from kiosk</span>
        </label>
        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onSave(initial ? { ...form, id: initial.id } : form)} disabled={saving || !form.name.trim() || !form.code.trim()}>{saving ? "Saving…" : initial ? "Save Changes" : "Create Station"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function CounterFormModal({ open, onClose, initial, stationId, stationName, onSave, saving }) {
  const [form, setForm] = useState({ name: "", number: "", is_active: true });
  useEffect(() => {
    if (open) setForm(initial ? { name: initial.name || "", number: initial.number || "", is_active: initial.is_active !== false } : { name: "", number: "", is_active: true });
  }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit Counter" : `New Counter — ${stationName}`} width="420px">
      <div style={F.form}>
        <FormField label="Counter Name" required><input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Counter 1" /></FormField>
        <FormField label="Counter Number" required hint="Shown on display board"><input style={inputStyle} type="number" value={form.number} onChange={e => set("number", e.target.value)} placeholder="1" /></FormField>
        {initial && (
          <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
            <span style={{ fontSize:"13px", color:"var(--text-secondary)", flex:1 }}>Active</span>
            <button onClick={() => set("is_active", !form.is_active)} style={{ width:"38px", height:"22px", borderRadius:"11px", border:"none", cursor:"pointer", position:"relative", flexShrink:0, transition:"background 0.2s", padding:0, background: form.is_active ? "#408DC9" : "var(--input-border)" }}>
              <div style={{ position:"absolute", top:"3px", width:"16px", height:"16px", borderRadius:"50%", background:"#fff", transition:"transform 0.2s", transform: form.is_active ? "translateX(18px)" : "translateX(2px)" }} />
            </button>
          </div>
        )}
        <div style={F.actions}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => onSave(initial ? { ...form, id: initial.id } : { ...form, station: stationId })} disabled={saving || !form.name.trim() || !form.number}>{saving ? "Saving…" : initial ? "Save" : "Create Counter"}</Btn>
        </div>
      </div>
    </Modal>
  );
}



// Relocates an existing counter to a different station — a plain
// reassignment, not delete-and-recreate, so the counter's history (any
// QueueJourney rows referencing it) stays intact. Physical counters/
// windows do get relocated between service areas in real facilities;
// this is the direct way to reflect that without losing records.
function MoveCounterModal({ open, onClose, counter, stations, onMove, saving }) {
  const [target, setTarget] = useState("");
  useEffect(() => { if (open) setTarget(""); }, [open, counter]);
  const otherStations = stations.filter(s => s.id !== counter?.currentStationId);

  return (
    <Modal open={open} onClose={onClose} title={`Move ${counter?.name || "Counter"}`} width="420px">
      <div style={F.form}>
        <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
          Currently at <strong style={{ color: "var(--text-primary)" }}>{counter?.currentStationName}</strong>.
          Choose a new station — nothing else about this counter changes.
        </div>
        <FormField label="Move to Station" required>
          <select style={selectStyle} value={target} onChange={e => setTarget(e.target.value)}>
            <option value="">— Select a station —</option>
            {otherStations.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </FormField>
        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onMove(target)} disabled={saving || !target}>{saving ? "Moving…" : "Move Counter"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function OutcomeFormModal({ open, onClose, initial, stationId, stationName, allStations, onSave, saving }) {
  const blank = { label_en: "", label_tw: "", next_station: "", is_exit: false, is_emergency: false, color: "default", sort_order: 0 };
  const [form, setForm] = useState(blank);
  useEffect(() => { if (open) setForm(initial ? { ...initial, next_station: initial.next_station || "" } : blank); }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit Outcome" : `New Outcome — ${stationName}`} width="480px">
      <div style={F.form}>
        <FormField label="Outcome Label (English)" required hint="e.g. Send to Vitals, Pharmacy, Done"><input style={inputStyle} value={form.label_en} onChange={e => set("label_en", e.target.value)} placeholder="e.g. OPD Visit — Vitals" /></FormField>
        {!BRAND.englishOnly && <FormField label="Outcome Label (Twi)"><input style={inputStyle} value={form.label_tw} onChange={e => set("label_tw", e.target.value)} placeholder="Twi label" /></FormField>}
        <div style={F.row2}>
          <FormField label="Next Station" hint="Leave blank if exit">
            <select style={selectStyle} value={form.next_station} onChange={e => set("next_station", e.target.value)}>
              <option value="">— None (exit) —</option>
              {allStations.filter(s => s.id !== stationId).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FormField>
          <FormField label="Button Color">
            <select style={selectStyle} value={form.color} onChange={e => set("color", e.target.value)}>
              <option value="default">Default</option>
              <option value="success">Green</option>
              <option value="warning">Orange</option>
              <option value="danger">Red</option>
            </select>
          </FormField>
        </div>
        <div style={{ display: "flex", gap: "20px" }}>
          <label style={F.checkbox}><input type="checkbox" checked={form.is_exit} onChange={e => set("is_exit", e.target.checked)} /><span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Exit (end of journey)</span></label>
          <label style={F.checkbox}><input type="checkbox" checked={form.is_emergency} onChange={e => set("is_emergency", e.target.checked)} /><span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>Emergency routing</span></label>
        </div>
        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={() => onSave(initial ? { ...form, id: initial.id } : { ...form, station: stationId })} disabled={saving || !form.label_en.trim()}>{saving ? "Saving…" : initial ? "Save" : "Create Outcome"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1100px" },
  zoneCard: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "14px", marginBottom: "14px", overflow: "hidden" },
  zoneHeader: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 20px", borderBottom: "1px solid rgba(64,141,201,0.08)" },
  expandBtn: { background: "none", border: "none", cursor: "pointer", padding: "4px", flexShrink: 0 },
  zoneInfo: { flex: 1, display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  zoneName: { fontSize: "15px", fontWeight: "700", color: "var(--text-primary)" },
  zoneDesc: { fontSize: "12px", color: "var(--text-muted)" },
  zoneCount: { fontSize: "11px", color: "var(--text-faint)", background: "var(--surface-border)", borderRadius: "4px", padding: "2px 7px" },
  stationList: { padding: "8px 0" },
  emptyInner: { padding: "16px 20px", fontSize: "13px", color: "var(--text-faint)", fontStyle: "italic" },
  stationRow: { display: "flex", alignItems: "center", gap: "12px", padding: "12px 20px", borderBottom: "1px solid rgba(64,141,201,0.05)" },
  stationLeft: { flex: 1, display: "flex", alignItems: "center", gap: "12px" },
  stationBadge: { background: "var(--surface-border)", color: "#8CBCE0", borderRadius: "6px", padding: "3px 8px", fontSize: "12px", fontWeight: "700", fontFamily: "monospace", flexShrink: 0 },
  stationName: { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "8px" },
  stationMeta: { fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" },
  counterList: {
    margin: "0 16px 8px 32px",
    display: "flex", flexDirection: "column", gap: "6px",
  },
  counterRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "8px 12px", borderRadius: "8px",
    background: "var(--surface-bg)", border: "1px solid rgba(64,141,201,0.08)",
  },
  entryChip: { fontSize: "10px", background: "rgba(34,197,94,0.12)", color: "#22C55E", borderRadius: "4px", padding: "2px 6px", fontWeight: "600" },
  rowActions: { display: "flex", gap: "6px", flexShrink: 0, flexWrap: "wrap" },
};

const F = {
  form: { display: "flex", flexDirection: "column", gap: "14px" },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  footer: { display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)", marginTop: "4px" },
  checkbox: { display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" },
};
